<template>
  <base-form class="c-context-select" @submit.prevent="onSubmit(preferredRoute)">
    <div class="c-context-select__body">
      <tabs v-model="localTabIndex">
        <tab-list class="xs-down:flex-wrap">
          <tab class="pt-2 xs-down:order-3">{{ $t('label.all') }}</tab>
          <tab class="pt-2 xs-down:order-4">{{ $t('label.recent') }}</tab>
          <tab class="pt-2 xs-down:order-5">{{ $t('label.favorites') }}</tab>

          <div class="c-context-select__search-wrapper xs-down:order-1 flex gap-x-4">
            <base-checkbox v-model="search.liveOnly" :disabled="localTabIndex > 0" v-if="localTabIndex == 0 && this.$store.state.authorization.policy.roles.length > 0" class="whitespace-nowrap mt-2">
              {{ $t('label.liveOnly') }}
            </base-checkbox>

            <base-input-group class="c-search-input c-search-input--size-sm c-search-input--show-icon" v-if="localTabIndex == 0">
              <font-awesome-icon class="c-search-input__icon" :icon="['fal', 'search']" />
              <base-input class="c-search-input__input" type="search" inputmode="search" v-model="search.filter._temporaryValue" @keydown.enter.prevent="onSearch" @input="onSearchDebounced" :placeholder="$t('label.search')" :disabled="localTabIndex > 0" />
            </base-input-group>

            <base-button v-if="canClose" @click="$emit('close')" class="c-context-select__header-close" aria-label="Close" variant="link">
              <span class="inline-block" aria-hidden="true"><font-awesome-icon :icon="['fal', 'times']" /></span>
            </base-button>
          </div>

          <div class="flex-break hidden xs-down:block xs-down:order-2"></div>
        </tab-list>
        <tab-container class="pt-0 -mt-px">
          <tab-content :is-lazy="true">
            <transition name="fade-fast-transition" mode="out-in">
              <decision-tree-selector v-bind:key="'full-' + search.liveOnly" v-if="search.isActive === false" :live-only="search.liveOnly" />
              <search-selector v-model="search.filter.value" v-bind:key="'search-' + search.liveOnly" v-if="search.isActive === true" :live-only="search.liveOnly" />
            </transition>
          </tab-content>
          <tab-content :is-lazy="true">
            <recent-selector />
          </tab-content>
          <tab-content :is-lazy="true">
            <favorite-selector />
          </tab-content>
        </tab-container>
      </tabs>
    </div>

    <div class="c-context-select__footer">
      <base-button v-if="canClose" variant="link-danger" class="mr-3" @click="$emit('close')">{{ $t('label.cancel') }}</base-button>
      <base-button type="submit" variant="secondary" is-pill class="btn-submit" :disabled="$v.$invalid || isLoading" :show-loader="isLoading">{{ $t('label.apply') }}</base-button>
    </div>
  </base-form>
</template>

<script>
import Vue from 'vue'
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import { required, minLength } from 'vuelidate/lib/validators'

import configurationService from 'Services/ConfigurationService'

import { favorite, recent } from '@/views/Context/Mixins'

import { TextFilter } from '@/classes/filters'
import { ConfigurationPolicy } from '@/classes/models'

import { DecisionTreeSelector, FavoriteSelector, RecentSelector, SearchSelector } from '@/views/Context/Components/ContextSelectors'
import { Tabs, TabContainer, TabContent, TabList, Tab } from '@/components/Tabs'

const debouncedSearch = debounce(func => func(), 400)

const state = Vue.observable({ localTabIndex: null, search: null })

export default {
  name: 'ContextSelect',

  components: { DecisionTreeSelector, FavoriteSelector, RecentSelector, SearchSelector, Tabs, TabContainer, TabContent, TabList, Tab },

  props: {
    tabIndex: {
      type: Number,
      default: 0
    },
    canClose: {
      type: Boolean,
      default: () => false
    },
    preferredRoute: {
      type: Object,
      default: null
    }
  },

  mixins: [ favorite, recent ],

  data () {
    return {
      isLoading: false
    }
  },

  created () {
    if (this.localTabIndex === null) {
      this.localTabIndex = this.tabIndex
    }

    if (this.search === null) {
      this.search = { isActive: false, liveOnly: true, filter: new TextFilter('search', this.$i18n.t('label.search'), 'searchTerm') }
    }
  },

  mounted () {
    if (typeof this.favoriteItems === 'undefined') {
      this.getFavoriteItems()
    }

    if (typeof this.recentItems === 'undefined') {
      this.getRecentItems()
    }
  },

  computed: {
    ...mapState({
      selections: state => state.context.selections,
      minimumSelections: state => state.context.minimumSelections
    }),

    search: {
      get () {
        return state.search
      },
      set (value) {
        state.search = value
      }
    },

    localTabIndex: {
      get () {
        return state.localTabIndex
      },
      set (value) {
        state.localTabIndex = value
      }
    }
  },

  methods: {
    /**
     * Get configuration.
     *
     * If there is any configuration policy, select the first one.
     */
    getConfiguration ({ context }) {
      return new Promise((resolve) => {
        this.$store.dispatch('setSelection', { key: 'configuration', policy: null })

        const { request } = configurationService.getAll(context)

        request.then(response => {
          const policies = response.data.map(configuration => {
            const { configurationId, tradeName, description, switches } = configuration
            return new ConfigurationPolicy({
              configurationId,
              tradeName,
              description,
              switches
            })
          })

          if (policies.length >= 1) {
            const { id, title, switches } = policies[0]
            this.$store.dispatch('setSelection', {
              key: 'configuration',
              policy: { id, title, switches }
            }).then(resolve)
          } else {
            this.$store.dispatch('setSelection', {
              key: 'configuration',
              policy: { id: null }
            }).then(resolve)
          }
        }).catch(() => {
          this.$store.dispatch('setSelection', { key: 'configuration', policy: { id: null } }).then(resolve)
        })
      })
    },

    /**
     * Submit form logic
     */
    onSubmit (route = null) {
      this.isLoading = true

      const merchant = this.$store.getters.getContextSelection('merchant')

      new Promise(resolve => { merchant && merchant.id !== null ? this.getConfiguration({ context: this.$store.getters.getContext() }).then(resolve) : resolve() }).then(() => {
        this.addRecent(this.$store.getters.getContext())

        this.$emit('close')

        this.$router.replace({ name: 'applyContext', params: { route, selections: this.selections } })
      })
    },

    onSearchDebounced () {
      debouncedSearch(() => this.onSearch())
    },

    onSearch (event) {
      debouncedSearch.cancel()

      if (event) {
        event.preventDefault()
      }

      this.search.filter.apply()
      this.search.isActive = this.search.filter.hasValue()
    }
  },

  validations () {
    return {
      selections: {
        required,
        minLength: minLength(1),
        $each: {
          isValid: selection => typeof selection.id !== 'undefined'
        }
      }
    }
  }
}

</script>

<style lang="postcss">
.flex-break {
  @apply h-0;
  flex-basis: 100%;
}
</style>
